import React, { useEffect, useContext, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  CircularProgress,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { AuthContext } from '../context/AuthContext';

const RequireAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading, refetchUser } = useContext(AuthContext);
  const [tokenProcessed, setTokenProcessed] = useState(false);
  const [processingToken, setProcessingToken] = useState(false);

  // Check for token in URL and process it
  useEffect(() => {
    const token = searchParams.get('token');
    
    // If token exists and we haven't processed it yet
    if (token && !tokenProcessed && !loading) {
      console.log('Processing token from URL for automatic login');
      setProcessingToken(true);
      
      // Save token to localStorage
      localStorage.setItem('token', token);
      
      // Mark as processed to prevent loops
      setTokenProcessed(true);
      
      // Remove token from URL but keep other params
      searchParams.delete('token');
      setSearchParams(searchParams, { replace: true });
      
      // Trigger user refetch with new token
      if (refetchUser) {
        refetchUser().finally(() => {
          setProcessingToken(false);
        });
      } else {
        setProcessingToken(false);
      }
    }
  }, [searchParams, tokenProcessed, loading, refetchUser, setSearchParams]);

  // If we're processing a token or loading user, show loading state
  if (processingToken || loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f9fafb',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: 'primary.main', mb: 3 }} />
          <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
            Authenticating...
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Please wait while we verify your credentials
          </Typography>
        </Box>
      </Box>
    );
  }

  // If user is authenticated after token processing, don't show the auth required screen
  if (user) {
    return null; // The parent route will handle the actual component rendering
  }

  const handleSignIn = () => {
    const currentPath = location.pathname + location.search;
    navigate(`/auth/sign-in?redirect=${encodeURIComponent(currentPath)}`);
  };

  const handleSignUp = () => {
    const currentPath = location.pathname + location.search;
    navigate(`/auth/sign-up?redirect=${encodeURIComponent(currentPath)}`);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb',
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'visible',
            position: 'relative',
          }}
        >
          {/* Lock Icon Circle */}
          <Box
            sx={{
              position: 'absolute',
              top: -40,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 25px rgba(59, 130, 246, 0.3)',
            }}
          >
            <LockIcon sx={{ fontSize: 40, color: '#ffffff' }} />
          </Box>

          <CardContent sx={{ pt: 8, pb: 4, px: 4, textAlign: 'center' }}>
            {/* Title */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                mb: 2,
              }}
            >
              Authentication Required
            </Typography>

            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                color: '#6b7280',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              You need to sign in to access this page. Create an account or log in to continue your journey with Priyo Work.
            </Typography>

            {/* Buttons */}
            <Stack spacing={2}>
              <Button
                variant="contained"
                size="large"
                startIcon={<LoginIcon />}
                onClick={handleSignIn}
                sx={{
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.39)',
                  '&:hover': {
                    backgroundColor: '#2563eb',
                    boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                  },
                }}
              >
                Sign In
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={<PersonAddIcon />}
                onClick={handleSignUp}
                sx={{
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: '#2563eb',
                    backgroundColor: 'rgba(59, 130, 246, 0.04)',
                  },
                }}
              >
                Create Account
              </Button>
            </Stack>

            {/* Additional Info */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e5e7eb' }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                }}
              >
                By continuing, you agree to our Terms of Service and Privacy Policy
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Back to Home Link */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="text"
            onClick={() => navigate('/')}
            sx={{
              color: '#6b7280',
              textTransform: 'none',
              '&:hover': {
                color: '#3b82f6',
                backgroundColor: 'transparent',
              },
            }}
          >
            ← Back to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default RequireAuth;
